import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ViewChild, ElementRef } from '@angular/core';
import { Router } from "@angular/router";
import { HttpResponse } from "@angular/common/http";
import { Subscription, BehaviorSubject, Observable, of, forkJoin } from "rxjs";
import { finalize, mergeMap, tap, switchMap } from "rxjs/operators";
import { FileQueueEntry, FileQueueKeyword } from "../../../common/models/onBaseIntegrationAndMapping.model";
import { PaymentService } from "../../../payments/services/payment.service";
import { PaymentSearchCriteria, ApplicationPayment, PaymentStatus } from "../../../payments/models/paymentItem.model";
import { LoaderService } from "../../../common/services/loader.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { ReceiptComponent } from "../../../common/components/receipt/receipt.component";
import { RegenerateRequeueRequest, InvoiceReceiptRegenerateResult } from "../../../reports/model/reports.model";
import { ProofOfPurchaseTypes } from "../../../payments/models/paymentItem.model";
import { DocumentManagementService } from "../../../common/services/document-management.service";
import { parseFileNameFromHeaders } from "../../../common/models/onBaseIntegrationAndMapping.model";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { AuthService } from "../../../services/auth.service";


@Component({
    selector: 'app-regenerate-invoice-receipt',
    templateUrl: './regenerate-invoice-receipt.component.html',
    styleUrls: ['./regenerate-invoice-receipt.component.scss'],
    standalone: false
})
export class RegenerateInvoiceReceiptComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    applicationPayments: Map<string, ApplicationPayment[]> = new Map<string, ApplicationPayment[]>();
    applicationQueueMap: Map<string, string> = new Map<string, string>();

    batchCount: number = 0;
    batchLimit: number = 10;
    currentBatch: number = 0;
    inProcessDialog: Dialog = new Dialog("Regeneration in progress.  Please wait before resubmitting.", [new DialogButton("OK", false, () => { return; })]);
    items: FileQueueEntry[] = [];
    loadingPayments: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    noDocumentsDialog: Dialog = new Dialog("No documents to resubmit.", [new DialogButton("OK", false, () => { return; })]);
    get paymentsLoading(): boolean {
        return this.loadingPayments.value;
    }

    payments: ApplicationPayment[] = [];
    processing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    queueIdentifiers: string[] = [];

    @ViewChildren(ReceiptComponent) receipts: QueryList<ReceiptComponent>;
    statuses = SubmissionStatus;
    subheader: SubHeader = new SubHeader({ title: "Regenerate Invoices & Receipts", buttons: [{ name: "Regenerate", method: () => this.regenerate() }] });
    submissionStatus: Map<string, InvoiceReceiptRegenerateResult> = new Map<string, InvoiceReceiptRegenerateResult>();
    subscription: Subscription = new Subscription();

    unpaidStatuses: any[] = [PaymentStatus.Pending, PaymentStatus.Overdue, PaymentStatus.Partial];

    waitDialog: Dialog = new Dialog("Please wait for data to finish loading.", [new DialogButton("OK", false, () => { return; })]);

    constructor(public router: Router, private paymentService: PaymentService, private loader: LoaderService, private subheaderService: SubHeaderService,
            dialog: DynamicDialogService, private documentService: DocumentManagementService, auth: AuthService) {
        super(dialog, auth);
        let state = this.router.getCurrentNavigation().extras.state;
        if (state == null) {
            console.error("no state");
        } else if (state.receipts == null) {
            console.error("no items in router state");
        } else {
            this.items = <FileQueueEntry[]>state.receipts;
            this.queueIdentifiers = this.items.map(i => i.identifier);
        }
        
    }

    ngOnInit() {
        this.confirmDialog = new Dialog("Invoice/Receipt regeneration in progress.  Leaving the page could cause regeneration to fail or abort prematurely.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
        this.subheaderService.updateSubHeader(this.subheader);
        let keywords: string[] = [];
        this.items.forEach(i => {
            let appWords = i.keywords.filter(k => k.keywordName.toLocaleLowerCase().includes('application id number'));
            appWords.forEach(a => {
                if (keywords.find(k => k === a.keywordValue) == null) {
                    keywords.push(a.keywordValue);
                }
            });
            
        });
        let obs$: Observable<ApplicationPayment[]> = of([]);
        if (keywords.length > 0) {
            let spec: PaymentSearchCriteria = new PaymentSearchCriteria("", "", [], []);
            spec.applicationNumbers = keywords;
            obs$ = this.paymentService.search(spec)
        }
        let subs$ = obs$.pipe(
                finalize(() => { this.loadingPayments.next(false); })
            ).subscribe(
            (data: ApplicationPayment[]) => {
                this.payments = data;                
                this.items.forEach(a => {
                    let appWord: FileQueueKeyword = a.keywords.find(k => k.keywordName.toLocaleLowerCase().includes('application id number'));
                    if (appWord != null) {
                        let payments = this.payments.filter(p => p.applicationNumber === appWord.keywordValue);
                        this.applicationPayments.set(a.identifier, payments);
                    }                  
                })
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(subs$);
    }

    getApplicationNumberByIdentifier(identifier: string): string {
        let applicationNumber: string = "";
        if (this.applicationPayments.has(identifier)) {
            let item = this.applicationPayments.get(identifier);
            if (item != null && item.length > 0) {
                applicationNumber = item[0].applicationNumber;
            }
        }
        return applicationNumber;
    }

    getPaymentsByIdentifier(identifier: string): ApplicationPayment[] {
        return this.applicationPayments.get(identifier);
    }    

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    getRegenerateModel(component: ReceiptComponent, content: string): RegenerateRequeueRequest {
        const model = new RegenerateRequeueRequest();
        model.html = `${component.shrinkFontStyle} ${content}`;
        model.existingQueueItem = component.queueIdentifier;
        model.organizationId = component.records[0].organizationId;
        model.organizationName = component.records[0].organization;
        model.applicationNumber = component.applicationNumber;
        model.useBanner = true;
        model.formatAsLetter = true;
        model.usePageNumberHeader = false;
        model.styleMergeFields = false;
        model.documentTypeName = (component.isPaid) ? component.records[0].receiptDocumentType : component.records[0].invoiceDocumentType;
        model.flowId = component.records[0].workflowId;
        model.isPaid = component.isPaid;
        return model;
    }

    getRegenerateObservable(models: RegenerateRequeueRequest[]): Observable<HttpResponse<Blob>[]> {
        let genObs$: Observable<HttpResponse<Blob>[]>= null;
        let obs$: Observable<RegenerateRequeueRequest[]> = of(models);
        genObs$ = obs$.pipe(
            switchMap((models: RegenerateRequeueRequest[]) => {
                models.forEach(m => { this.submissionStatus.set(m.existingQueueItem, null); });
                return this.paymentService.batchReproduceInvoicesAndReceipts(models)
            }),
            switchMap((results: InvoiceReceiptRegenerateResult[]) => {
                let blobObs$: Observable<HttpResponse<Blob>>[] = [];
                results.forEach(r => {
                    this.submissionStatus.set(r.queueItem, r);
                    if (r.exception == null) {
                        let download$: Observable<HttpResponse<Blob>> = this.documentService.downloadFromQueue(r.queueItem)
                            .pipe(tap((data: any) => {
                                const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                                const blob = new Blob([data.body], { type: data.body.type });
								const uri = window.URL.createObjectURL(blob);
								let link: HTMLAnchorElement = null
								let component = this.receipts.find(rec => rec.queueIdentifier === r.queueItem);
								if (component == null) {
									link = document.createElement("a");
								} else {
									link = component.downloadLink.nativeElement;
								}
								link.href = uri;
								link.download = r.fileName;
								link.click();
                            }));
                        blobObs$.push(download$);
                    } else {
                        blobObs$.push(of(null));
                    }
                });

                return forkJoin(blobObs$);
            })
        )
        return genObs$;
    }

    getSubmissionStatus(id: string): SubmissionStatus {
        let status: SubmissionStatus = SubmissionStatus.Unknown;
        if (this.submissionStatus.has(id)) {
            let result: InvoiceReceiptRegenerateResult = this.submissionStatus.get(id);
            if (result == null) {
                status = SubmissionStatus.InProcess;
            } else if (result.exception == null) {
                status = SubmissionStatus.Success;
            } else {
                status = SubmissionStatus.Error;
            }
        } else {
            status = SubmissionStatus.Unsubmitted;
        }
        return status;
    }

    receiptIsPaid(identifier: string): boolean {
        let payments = this.getPaymentsByIdentifier(identifier);
        return payments != null && payments.length > 0 && payments.find(p => p.paymentStatus === PaymentStatus.Paid || p.paymentStatus === PaymentStatus.Partial) != null;
    }

    regenerate(): void {
        if (this.paymentsLoading === true) {
            this.waitDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.waitDialog);
        } else if (this.processing.value === true) {
            this.inProcessDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.inProcessDialog);
        
        } else {
            let allDocObs$: Observable<HttpResponse<Blob>[]>[] = [];
            let batch: RegenerateRequeueRequest[] = [];
            let receiptList: ReceiptComponent[] = [];
            this.receipts.forEach(r => { receiptList.push(r); });
            for (let i = 0; i < receiptList.length; i++) {
                let component = receiptList[i];
                let submitDocument: boolean = true;
                if (this.submissionStatus.has(component.queueIdentifier)) {
                    let result = this.submissionStatus.get(component.queueIdentifier);
                    submitDocument = result == null || result.exception != null;
                }
                if (submitDocument) {                    
                    if (batch.length === this.batchLimit) {
                        allDocObs$.push(this.getRegenerateObservable(batch));
                        batch = [];
                    }
                    let docType = component.isPaid ? ProofOfPurchaseTypes.Receipt : ProofOfPurchaseTypes.Invoice;
                    let content = component.getContent(docType);
                    if (content && content.innerHTML) {
                        let model = this.getRegenerateModel(component, content.innerHTML);
                        batch.push(model);
                    }
                    if (i === receiptList.length - 1) {                        
                        allDocObs$.push(this.getRegenerateObservable(batch));
                    }
                }
            }          

            if (allDocObs$.length > 0) {
                let batchObs$: Observable<[HttpResponse<Blob>[]]> =
                    of(allDocObs$.length).pipe(
                        tap((b: number) => {
                            this.batchCount = b;
                            this.currentBatch = 1;
                        }),
                        switchMap(() => {
                            return forkJoin(allDocObs$[0]);
                        }));
                    
                for (let b = 1; b < allDocObs$.length; b++) {
                    batchObs$ = batchObs$.pipe(
                        switchMap(() => {
                            return of(b);
                        }),
                        mergeMap((batchNumber: number) => {
                            this.currentBatch = batchNumber + 1;
                            return forkJoin(allDocObs$[b]);
                        })
                    )
                }
                this.batchCount = allDocObs$.length;
                this.allowDeactivate.next(false);
                this.processing.next(true);
                let subs$ = batchObs$.pipe(                    
                    finalize(() => {
                        this.processing.next(false);
                        this.allowDeactivate.next(true);
                        this.currentBatch = 0;
                        this.batchCount = 0;
                    })
                ).subscribe(
                    (results: any) => {                       
                        console.log('success');
                        
                    },
                    (error) => {
                        console.error(error);
                        this.dialogService.showError();
                    }
                );
                this.subscription.add(subs$);
            } else {
                this.noDocumentsDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.noDocumentsDialog);
            }

        }
    }
}

enum SubmissionStatus {
    Unknown = "Unknown",
    Unsubmitted = "Unsubmitted",
    InProcess = "In Process",
    Success = "Successful",
    Error = "Error"
}
